import { useMutation, useQueryClient } from '@tanstack/react-query'
import apiClient from '@/apiClient'
import type {
  InventoryImportOptions,
  InventoryImportPreview,
  InventoryImportCommitResult,
} from '@/types/backend'
import { containerQK } from './useContainers'

export const inventoryQK = {
  importPreview: (fingerprint: string) =>
    ['inventory', '_import', 'preview', fingerprint] as const,
}

export const usePreviewInitialStockImportMutation = () =>
  useMutation({
    mutationFn: async (payload: {
      file: File
      options?: InventoryImportOptions
    }) => {
      const fd = new FormData()
      fd.append('file', payload.file)

      const o = payload.options ?? {}
      if (o.delimiter) fd.append('delimiter', o.delimiter)
      if (o.mode) fd.append('mode', o.mode) // ex: UPSERT_BY_SKU / MATCH_BY_NAME
      if (o.containerId) fd.append('containerId', o.containerId)
      if (o.columns) fd.append('columns', JSON.stringify(o.columns))
      if (o.autoExpense !== undefined)
        fd.append('autoExpense', String(o.autoExpense))

      const { data } = await apiClient.post<InventoryImportPreview>(
        '/inventory/_import/preview',
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      )
      return data
    },
  })

export const useCommitInitialStockImportMutation = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (payload: {
      file: File
      options?: InventoryImportOptions
    }) => {
      const fd = new FormData()
      fd.append('file', payload.file)

      const o = payload.options ?? {}
      if (o.delimiter) fd.append('delimiter', o.delimiter)
      if (o.mode) fd.append('mode', o.mode)
      if (o.containerId) fd.append('containerId', o.containerId)
      if (o.columns) fd.append('columns', JSON.stringify(o.columns))
      if (o.autoExpense !== undefined)
        fd.append('autoExpense', String(o.autoExpense))

      const { data } = await apiClient.post<InventoryImportCommitResult>(
        '/inventory/_import/commit',
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      )
      return data
    },
    onSuccess: (_res, vars) => {
      qc.invalidateQueries({ queryKey: ['inventory'] })
      qc.invalidateQueries({ queryKey: ['items'] })
      if (vars?.options?.containerId) {
        qc.invalidateQueries({
          queryKey: containerQK.one(vars.options.containerId),
        })
      }
    },
  })
}
